'use client'

import type {
  AnimationConfigWithData,
  AnimationConfigWithPath,
  AnimationDirection,
  AnimationEventCallback,
  AnimationItem,
  LottiePlayer,
} from 'lottie-web'
import type { DetailedHTMLProps, HTMLAttributes } from 'react'
import * as React from 'react'

/**
 * Animation control methods exposed via ref
 */
export interface LottieAnimationHandle {
  /** Play the animation */
  play: () => void
  /** Pause the animation */
  pause: () => void
  /** Stop the animation and reset to first frame */
  stop: () => void
  /** Set animation speed (1 = normal, 2 = 2x speed, etc.) */
  setSpeed: (speed: number) => void
  /** Set animation direction (1 = forward, -1 = reverse) */
  setDirection: (direction: AnimationDirection) => void
  /** Go to a specific frame and play */
  goToAndPlay: (frame: number, isFrame?: boolean) => void
  /** Go to a specific frame and stop */
  goToAndStop: (frame: number, isFrame?: boolean) => void
  /** Get the underlying animation instance */
  getAnimation: () => AnimationItem | null
}

/**
 * Event handlers for animation lifecycle
 */
export interface LottieAnimationEvents {
  /** Called when animation completes */
  onComplete?: AnimationEventCallback
  /** Called on each loop iteration */
  onLoopComplete?: AnimationEventCallback
  /** Called when animation enters a frame */
  onEnterFrame?: AnimationEventCallback
  /** Called when a segment starts */
  onSegmentStart?: AnimationEventCallback
  /** Called when animation data is loaded */
  onDataReady?: () => void
  /** Called if animation fails to load */
  onDataFailed?: () => void
  /** Called when lottie-web library fails to load */
  onLoadError?: (error: Error) => void
}

export interface LottieAnimationProps
  extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'>, LottieAnimationEvents {
  /** Animation configuration (path or data) */
  animationConfig:
    | Omit<AnimationConfigWithPath<'svg'>, 'container' | 'renderer'>
    | Omit<AnimationConfigWithData<'svg'>, 'container' | 'renderer'>
  /** Initial animation speed (default: 1) */
  speed?: number
  /** Initial animation direction (default: 1) */
  direction?: AnimationDirection
  /** Whether to show a loading state (renders children while loading) */
  showLoadingState?: boolean
  /** ARIA label for accessibility */
  ariaLabel?: string
  /** ARIA role (default: 'img') */
  ariaRole?: string
}

/**
 * A robust Lottie animation component with full control and error handling
 *
 * @example
 * ```tsx
 * const animationRef = useRef<LottieAnimationHandle>(null)
 *
 * <LottieAnimation
 *   ref={animationRef}
 *   animationConfig={{ path: '/animation.json', loop: true, autoplay: true }}
 *   onComplete={() => console.log('Animation completed')}
 *   ariaLabel="Loading animation"
 * />
 * ```
 */
export const LottieAnimation = React.forwardRef<LottieAnimationHandle, LottieAnimationProps>(
  (
    {
      animationConfig,
      speed = 1,
      direction = 1,
      showLoadingState = false,
      ariaLabel,
      ariaRole = 'img',
      onComplete,
      onLoopComplete,
      onEnterFrame,
      onSegmentStart,
      onDataReady,
      onDataFailed,
      onLoadError,
      children,
      ...props
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const animationRef = React.useRef<AnimationItem | null>(null)
    const [lottie, setLottie] = React.useState<LottiePlayer | null>(null)
    const [isLoading, setIsLoading] = React.useState(true)
    const [hasError, setHasError] = React.useState(false)

    // Memoize animation config by value (not reference) to prevent re-initialization
    // when parent re-renders with equivalent inline config (e.g. due to countdown state)
    const configPath = 'path' in animationConfig ? animationConfig.path : undefined

    const configData = 'animationData' in animationConfig ? animationConfig.animationData : undefined
    const memoizedConfig = React.useMemo(
      () => animationConfig,
      [configPath, configData, animationConfig.loop, animationConfig.autoplay],
    )

    // Load lottie-web library
    React.useEffect(() => {
      let isMounted = true

      import('lottie-web')
        .then((Lottie) => {
          if (isMounted) {
            setLottie(Lottie.default)
            setIsLoading(false)
          }
        })
        .catch((error) => {
          if (isMounted) {
            setHasError(true)
            setIsLoading(false)
            onLoadError?.(error instanceof Error ? error : new Error('Failed to load lottie-web'))
          }
        })

      return () => {
        isMounted = false
      }
    }, [onLoadError])

    // Initialize and manage animation
    React.useEffect(() => {
      if (!lottie || !containerRef.current || hasError) {
        return
      }

      // Clear container before loading new animation
      containerRef.current.innerHTML = ''

      try {
        const animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          ...memoizedConfig,
        })

        animationRef.current = animation

        // Set initial speed and direction
        animation.setSpeed(speed)
        animation.setDirection(direction)

        // Register event listeners
        if (onComplete) animation.addEventListener('complete', onComplete)
        if (onLoopComplete) animation.addEventListener('loopComplete', onLoopComplete)
        if (onEnterFrame) animation.addEventListener('enterFrame', onEnterFrame)
        if (onSegmentStart) animation.addEventListener('segmentStart', onSegmentStart)
        if (onDataReady) animation.addEventListener('data_ready', onDataReady)
        if (onDataFailed) animation.addEventListener('data_failed', onDataFailed)

        return () => {
          // Clean up event listeners
          if (onComplete) animation.removeEventListener('complete', onComplete)
          if (onLoopComplete) animation.removeEventListener('loopComplete', onLoopComplete)
          if (onEnterFrame) animation.removeEventListener('enterFrame', onEnterFrame)
          if (onSegmentStart) animation.removeEventListener('segmentStart', onSegmentStart)
          if (onDataReady) animation.removeEventListener('data_ready', onDataReady)
          if (onDataFailed) animation.removeEventListener('data_failed', onDataFailed)

          animation.destroy()
          animationRef.current = null
        }
      } catch (error) {
        setHasError(true)
        onLoadError?.(error instanceof Error ? error : new Error('Failed to load animation'))
      }
    }, [
      lottie,
      memoizedConfig,
      speed,
      direction,
      hasError,
      onComplete,
      onLoopComplete,
      onEnterFrame,
      onSegmentStart,
      onDataReady,
      onDataFailed,
      onLoadError,
    ])

    // Expose imperative handle for parent components
    React.useImperativeHandle(
      ref,
      () => ({
        play: () => animationRef.current?.play(),
        pause: () => animationRef.current?.pause(),
        stop: () => animationRef.current?.stop(),
        setSpeed: (newSpeed: number) => animationRef.current?.setSpeed(newSpeed),
        setDirection: (newDirection: AnimationDirection) => animationRef.current?.setDirection(newDirection),
        goToAndPlay: (frame: number, isFrame?: boolean) => animationRef.current?.goToAndPlay(frame, isFrame),
        goToAndStop: (frame: number, isFrame?: boolean) => animationRef.current?.goToAndStop(frame, isFrame),
        getAnimation: () => animationRef.current,
      }),
      [],
    )

    // Show loading state if enabled and still loading
    if (showLoadingState && isLoading) {
      return (
        <div {...props} role='status' aria-label={ariaLabel ?? 'Loading animation'}>
          {children ?? <span>Loading animation...</span>}
        </div>
      )
    }

    // Don't render if there was an error
    if (hasError) {
      return null
    }

    return <div ref={containerRef} role={ariaRole} aria-label={ariaLabel} aria-busy={isLoading} {...props} />
  },
)

LottieAnimation.displayName = 'LottieAnimation'
